<template>
  <v-container fluid>
    <v-card class="graphql-tester">
      <v-card-title class="primary white--text">
        <v-icon color="white" class="mr-2">{{ icons.mdiGraphql }}</v-icon>
        <span class="white--text">Shopify GraphQL API Tester</span>
      </v-card-title>

      <v-card-text class="pt-6">
        <!-- Shop Selection -->
        <v-row>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="selectedShop"
              :items="activeShops"
              :search-input.sync="shopSearch"
              :loading="loadingShops"
              :item-text="getShopDisplayName"
              item-value="id"
              label="Select Shop"
              placeholder="Type to search shops..."
              outlined
              dense
              clearable
              @change="onShopChange"
            >
              <template v-slot:prepend-inner>
                <v-icon small>{{ icons.mdiStore }}</v-icon>
              </template>
              <template v-slot:selection="{ item }">
                <span>{{ getShopDisplayName(item) }}</span>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ getShopDisplayName(item) }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.specifier || item.shop_url || 'N/A' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ shopSearch && shopSearch.length > 0 ? 'No shops found' : 'Type to search shops' }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="6">
            <v-select
              v-model="apiVersion"
              :items="apiVersions"
              label="API Version"
              outlined
              dense
            >
              <template v-slot:prepend-inner>
                <v-icon small>{{ icons.mdiApi }}</v-icon>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <!-- Query Type Selection -->
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="queryType"
              :items="queryTypes"
              item-text="label"
              item-value="value"
              label="Select Query Type"
              outlined
              dense
              @change="onQueryTypeChange"
            >
              <template v-slot:prepend-inner>
                <v-icon small>{{ icons.mdiCodeBraces }}</v-icon>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" md="6" v-if="queryType === 'product'">
            <v-autocomplete
              v-model="selectedProduct"
              :items="products"
              :loading="loadingProducts"
              :search-input.sync="productSearch"
              item-text="title"
              item-value="shopify_id"
              label="Search or Select Product"
              placeholder="Type to search products..."
              outlined
              dense
              clearable
              @change="onProductSelect"
            >
              <template v-slot:prepend-inner>
                <v-icon small>{{ icons.mdiCube }}</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-avatar>
                  <v-img :src="getProductImage(item.attributes) || require('@/assets/images/misc/no-image.png')"></v-img>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                  <v-list-item-subtitle>ID: {{ item.shopify_id }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      Type to search products or enter Shopify ID manually below
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="6" v-if="queryType === 'product'">
            <v-text-field
              v-model="productId"
              label="Product ID (Manual Entry)"
              placeholder="Enter Shopify Product ID"
              outlined
              dense
              type="number"
              hint="Enter numeric ID only (e.g., 10044873539894)"
            >
              <template v-slot:prepend-inner>
                <v-icon small>{{ icons.mdiNumeric }}</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" md="6" v-if="queryType === 'order'">
            <v-text-field
              v-model="orderId"
              label="Order ID (Required)"
              placeholder="Enter Shopify Order ID"
              outlined
              dense
              type="number"
              required
              :rules="[v => !!v || 'Order ID is required']"
            >
              <template v-slot:prepend-inner>
                <v-icon small>{{ icons.mdiNumeric }}</v-icon>
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <!-- Field Selection Checkboxes (Product Only) -->
        <v-row v-if="queryType === 'product'">
          <v-col cols="12">
            <v-card outlined class="pa-3">
              <div class="mb-2">
                <strong>Include Fields:</strong>
              </div>
              <v-row>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="includeFields.metafields"
                    label="Product Metafields"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="includeFields.variants"
                    label="Variants"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="includeFields.media"
                    label="Media"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="includeFields.variantMetafields"
                    label="Variant Metafields"
                    dense
                    hide-details
                    :disabled="!includeFields.variants"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Dynamic Parameters -->
        <v-row v-if="queryType === 'product'">
          <v-col cols="12">
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon small class="mr-2">{{ icons.mdiCog }}</v-icon>
                    <strong>Query Parameters (Optional)</strong>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="4" v-if="includeFields.metafields">
                      <v-text-field
                        v-model.number="queryParams.metafieldsPageSize"
                        label="Metafields Count"
                        type="number"
                        outlined
                        dense
                        :max="250"
                        :min="1"
                        hint="Max: 250, Default: 25"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" v-if="includeFields.variants">
                      <v-text-field
                        v-model.number="queryParams.variantsPageSize"
                        label="Variants Count"
                        type="number"
                        outlined
                        dense
                        :max="250"
                        :min="1"
                        hint="Max: 250, Default: 25"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" v-if="includeFields.media">
                      <v-text-field
                        v-model.number="queryParams.mediaPageSize"
                        label="Media Count"
                        type="number"
                        outlined
                        dense
                        :max="250"
                        :min="1"
                        hint="Max: 250, Default: 25"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <!-- Request Details Display with Tabs -->
        <v-row v-if="selectedShop && queryType">
          <v-col cols="12">
            <v-card outlined class="mt-4">
              <v-card-title class="subtitle-1 grey lighten-4 d-flex align-center">
                <v-icon small class="mr-2">{{ icons.mdiInformation }}</v-icon>
                Request Details
                <v-spacer></v-spacer>
                <div class="d-flex align-center mr-4">
                  <strong class="mr-2">Endpoint:</strong>
                  <v-chip small color="primary" class="mr-2">POST</v-chip>
                  <code class="pa-1 grey lighten-3 text-caption">{{ apiEndpoint }}</code>
                </div>
                <v-btn
                  large
                  color="success"
                  :loading="loadingResponse"
                  :disabled="!canSendRequest"
                  @click="sendRequest"
                >
                  <v-icon left>{{ icons.mdiSend }}</v-icon>
                  Send Request
                </v-btn>
              </v-card-title>
              
              <v-tabs v-model="activeTab" class="px-4">
                <v-tab>
                  <v-icon small class="mr-2">{{ icons.mdiCodeBraces }}</v-icon>
                  Body
                </v-tab>
                <v-tab>
                  <v-icon small class="mr-2">{{ icons.mdiCodeTags }}</v-icon>
                  Variables
                </v-tab>
                <v-tab>
                  <v-icon small class="mr-2">{{ icons.mdiFileDocument }}</v-icon>
                  Headers
                </v-tab>
                <v-tab>
                  <v-icon small class="mr-2">{{ icons.mdiCog }}</v-icon>
                  Params
                </v-tab>
                <v-tab v-if="response">
                  <v-icon small class="mr-2" :color="response.success ? 'success' : 'error'">
                    {{ response.success ? icons.mdiCheckCircle : icons.mdiAlertCircle }}
                  </v-icon>
                  Response
                  <v-chip
                    x-small
                    :color="response.success ? 'success' : 'error'"
                    text-color="white"
                    class="ml-2"
                  >
                    {{ response.success ? 'Success' : 'Error' }}
                  </v-chip>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="activeTab">
                <!-- Body Tab -->
                <v-tab-item>
                  <v-card-text class="pa-0">
                    <div class="request-detail pa-4">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <strong>GraphQL Query:</strong>
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="copyToClipboard(graphqlQuery)"
                        >
                          <v-icon small>{{ icons.mdiContentCopy }}</v-icon>
                          Copy Query
                        </v-btn>
                      </div>
                      <v-card outlined class="query-card">
                        <pre class="query-display"><code>{{ graphqlQuery }}</code></pre>
                      </v-card>
                    </div>
                  </v-card-text>
                </v-tab-item>

                <!-- Variables Tab -->
                <v-tab-item>
                  <v-card-text class="pa-0">
                    <div class="request-detail pa-4">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <strong>Variables:</strong>
                        <v-btn
                          small
                          text
                          color="primary"
                          v-if="graphqlVariables && Object.keys(graphqlVariables).length"
                          @click="copyToClipboard(JSON.stringify(graphqlVariables, null, 2))"
                        >
                          <v-icon small>{{ icons.mdiContentCopy }}</v-icon>
                          Copy Variables
                        </v-btn>
                      </div>
                      <v-card outlined class="query-card">
                        <pre class="variables-display" v-if="graphqlVariables && Object.keys(graphqlVariables).length">
                          <code>{{ JSON.stringify(graphqlVariables, null, 2) }}</code>
                        </pre>
                        <div v-else class="pa-4 text-center grey--text">
                          No variables for this query
                        </div>
                      </v-card>
                    </div>
                  </v-card-text>
                </v-tab-item>

                <!-- Headers Tab -->
                <v-tab-item>
                  <v-card-text class="pa-0">
                    <div class="request-detail pa-4">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <strong>Headers:</strong>
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="copyToClipboard(headersDisplay)"
                        >
                          <v-icon small>{{ icons.mdiContentCopy }}</v-icon>
                          Copy Headers
                        </v-btn>
                      </div>
                      <v-card outlined class="query-card">
                        <pre class="headers-display"><code>{{ headersDisplay }}</code></pre>
                      </v-card>
                    </div>
                  </v-card-text>
                </v-tab-item>

                <!-- Params Tab -->
                <v-tab-item>
                  <v-card-text class="pa-0">
                    <div class="request-detail pa-4">
                      <strong class="mb-2 d-block">Request Parameters:</strong>
                      <v-simple-table>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Parameter</th>
                              <th class="text-left">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>Method</strong></td>
                              <td><v-chip small color="primary">POST</v-chip></td>
                            </tr>
                            <tr>
                              <td><strong>URL</strong></td>
                              <td><code>{{ apiEndpoint }}</code></td>
                            </tr>
                            <tr>
                              <td><strong>API Version</strong></td>
                              <td>{{ apiVersion }}</td>
                            </tr>
                            <tr>
                              <td><strong>Query Type</strong></td>
                              <td>{{ queryType }}</td>
                            </tr>
                            <tr v-if="queryType === 'product' && includeFields.metafields">
                              <td><strong>Metafields Count</strong></td>
                              <td>{{ queryParams.metafieldsPageSize }}</td>
                            </tr>
                            <tr v-if="queryType === 'product' && includeFields.variants">
                              <td><strong>Variants Count</strong></td>
                              <td>{{ queryParams.variantsPageSize }}</td>
                            </tr>
                            <tr v-if="queryType === 'product' && includeFields.media">
                              <td><strong>Media Count</strong></td>
                              <td>{{ queryParams.mediaPageSize }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </div>
                  </v-card-text>
                </v-tab-item>

                <!-- Response Tab -->
                <v-tab-item v-if="response">
                  <v-card-text class="pa-0">
                    <div class="request-detail pa-4">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <div class="d-flex align-center">
                          <v-icon 
                            small 
                            class="mr-2" 
                            :color="response.success ? 'success' : 'error'"
                          >
                            {{ response.success ? icons.mdiCheckCircle : icons.mdiAlertCircle }}
                          </v-icon>
                          <strong>Response {{ response.success ? 'Success' : 'Error' }}</strong>
                          <v-chip
                            x-small
                            :color="response.success ? 'success' : 'error'"
                            text-color="white"
                            class="ml-2"
                          >
                            {{ response.success ? 'Success' : 'Error' }}
                          </v-chip>
                        </div>
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="copyToClipboard(JSON.stringify(response, null, 2))"
                        >
                          <v-icon small>{{ icons.mdiContentCopy }}</v-icon>
                          Copy Response
                        </v-btn>
                      </div>
                      <v-card outlined class="response-card">
                        <div class="response-display">
                          <json-viewer
                            :value="response"
                            :expand-depth="3"
                            copyable
                            boxed
                            sort
                          ></json-viewer>
                        </div>
                      </v-card>
                    </div>
                  </v-card-text>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import store from '@/store'
import {
  mdiGraphql,
  mdiStore,
  mdiApi,
  mdiCodeBraces,
  mdiCube,
  mdiNumeric,
  mdiCog,
  mdiInformation,
  mdiSend,
  mdiCheckCircle,
  mdiAlertCircle,
  mdiContentCopy,
  mdiCodeTags,
  mdiFileDocument
} from '@mdi/js'
import JsonViewer from 'vue-json-viewer'
import { debounce } from 'lodash'
import { SHOPIFY_API_VERSION } from '@/utils/shopifyGraphql'

export default {
  name: 'GraphqlTester',
  components: {
    JsonViewer
  },
  data() {
    return {
      icons: {
        mdiGraphql,
        mdiStore,
        mdiApi,
        mdiCodeBraces,
        mdiCube,
        mdiNumeric,
        mdiCog,
        mdiInformation,
        mdiSend,
        mdiCheckCircle,
        mdiAlertCircle,
        mdiContentCopy,
        mdiCodeTags,
        mdiFileDocument
      },
      activeTab: 0,
      includeFields: {
        metafields: true,
        variants: true,
        media: true,
        variantMetafields: true
      },
      activeShops: [],
      selectedShop: null,
      loadingShops: false,
      shopSearch: null,
      shops: {
        current_page: 1,
        data: [],
        total: 0
      },
      apiVersions: [SHOPIFY_API_VERSION, '2025-01'],
      apiVersion: SHOPIFY_API_VERSION,
      queryTypes: [
        { label: 'Product Query', value: 'product' },
        // { label: 'Order Query', value: 'order' }
      ],
      queryType: null,
      products: [],
      loadingProducts: false,
      productSearch: null,
      selectedProduct: null,
      productId: '',
      orderId: '',
      queryParams: {
        metafieldsPageSize: 25,
        variantsPageSize: 25,
        mediaPageSize: 25
      },
      loadingResponse: false,
      response: null,
      shopData: null
    }
  },
  computed: {
    apiEndpoint() {
      if (!this.shopData) return ''
      const shopUrl = this.shopData.shop_url || this.shopData.specifier || ''
      if (!shopUrl) return 'N/A'
      return `https://${shopUrl}/admin/api/${this.apiVersion}/graphql.json`
    },
    headersDisplay() {
      if (!this.shopData) return ''
      const token = this.shopData.access_token || this.shopData.token || ''
      const maskedToken = token ? '***' + token.slice(-4) : '****'
      return JSON.stringify({
        'Content-Type': 'application/json',
        'X-Shopify-Access-Token': maskedToken
      }, null, 2)
    },
    graphqlQuery() {
      if (!this.queryType) return ''
      
      if (this.queryType === 'product') {
        return this.getProductQuery()
      } else if (this.queryType === 'order') {
        return this.getOrderQuery()
      }
      return ''
    },
    graphqlVariables() {
      if (!this.queryType) return {}
      
      if (this.queryType === 'product') {
        const id = this.selectedProduct || this.productId
        if (!id) return {}
        const vars = {
          id: `gid://shopify/Product/${id}`
        }
        
        if (this.includeFields.metafields) {
          vars.metafieldsPageSize = this.queryParams.metafieldsPageSize
        }
        if (this.includeFields.variants) {
          vars.variantsPageSize = this.queryParams.variantsPageSize
        }
        if (this.includeFields.media) {
          vars.mediaPageSize = this.queryParams.mediaPageSize
        }
        
        return vars
      } else if (this.queryType === 'order') {
        if (!this.orderId) return {}
        return {
          id: `gid://shopify/Order/${this.orderId}`
        }
      }
      return {}
    },
    canSendRequest() {
      if (!this.selectedShop || !this.queryType) return false
      
      if (this.queryType === 'product') {
        return !!(this.selectedProduct || this.productId)
      } else if (this.queryType === 'order') {
        return !!this.orderId
      }
      return false
    }
  },
  watch: {
    productSearch(val) {
      if (val && val.length > 2) {
        this.fetchProducts(val)
      }
    },
    shopSearch(val) {
      if (val !== null && val !== undefined) {
        this.debouncedShopSearch()
      }
    }
  },
  async mounted() {
    await this.fetchActiveShops()
    
    // Debounce shop search using lodash
    this.debouncedShopSearch = debounce(() => {
      this.fetchActiveShops()
    }, 500)
  },
  methods: {
    getShopDisplayName(shop) {
      if (!shop) return ''
      // Try different possible field names for shop name
      return shop.shop_name || 
             shop.name || 
             shop.specifier || 
             (shop.uninstalled_client && shop.uninstalled_client.shop_name) || 
             `Shop #${shop.id}`
    },
    async fetchActiveShops() {
      try {
        this.loadingShops = true
        
        // Use the same API pattern as ShopsList.vue
        const response = await axios.get('admin/shops', {
          params: {
            page: this.shops.current_page,
            keyword: this.shopSearch || '',
            state: 'Active' // Filter for active shops only
          }
        })
        
        if (response.data.success) {
          const shopsData = response.data.shops.data || response.data.shops || []
          this.activeShops = shopsData
          this.shops = {
            ...this.shops,
            total: response.data.shops.total || shopsData.length
          }
          
          // Set default shop to logged-in shop after shops are loaded (only on initial load)
          if (!this.selectedShop) {
            const loggedInShop = store.getters.loggedInShop
            if (loggedInShop && this.activeShops.length > 0) {
              const foundShop = this.activeShops.find(s => s.id === loggedInShop.id)
              if (foundShop) {
                this.selectedShop = loggedInShop.id
                this.onShopChange()
              } else if (this.activeShops.length > 0) {
                this.selectedShop = this.activeShops[0].id
                this.onShopChange()
              }
            } else if (this.activeShops.length > 0) {
              this.selectedShop = this.activeShops[0].id
              this.onShopChange()
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch shops:', error)
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch active shops'
        })
      } finally {
        this.loadingShops = false
      }
    },
    async onShopChange() {
      const shop = this.activeShops.find(s => s.id === this.selectedShop)
      if (shop) {
        this.shopData = shop
      }
      this.response = null
    },
    async fetchProducts(search) {
      if (!this.selectedShop) return
      
      try {
        this.loadingProducts = true
        const response = await axios.get(`admin/shops/${this.selectedShop}/get-shopify-products`, {
          params: {
            search_keyword: search,
            per_page: 50
          }
        })
        
        if (response.data.success) {
          this.products = response.data.products.data || []
        }
      } catch (error) {
        console.error('Failed to fetch products:', error)
      } finally {
        this.loadingProducts = false
      }
    },
    onQueryTypeChange() {
      this.response = null
      this.selectedProduct = null
      this.productId = ''
      this.orderId = ''
      // Reset include fields for product queries
      if (this.queryType === 'product') {
        this.includeFields = {
          metafields: true,
          variants: true,
          media: true,
          variantMetafields: true
        }
      }
    },
    onProductSelect() {
      if (this.selectedProduct) {
        this.productId = ''
      }
    },
    getProductImage(attributes) {
      try {
        const parsed = JSON.parse(attributes)
        if (parsed.images && Array.isArray(parsed.images) && parsed.images[0]) {
          return parsed.images[0].src
        }
        return parsed?.image_url || ''
      } catch (e) {
        return ''
      }
    },
    getProductQuery() {
      const variables = ['$id: ID!']
      const queryFields = [
        'id',
        'title',
        'handle',
        'status',
        'createdAt',
        'updatedAt',
        'descriptionHtml',
        'vendor',
        'productType',
        'tags'
      ]

      // Add metafields if enabled
      if (this.includeFields.metafields) {
        variables.push('$metafieldsPageSize: Int = 25')
        queryFields.push(`metafields(first: $metafieldsPageSize) {
      edges {
        node {
          id
          namespace
          key
          type
          value
          createdAt
          updatedAt
        }
      }
    }`)
      }

      // Add variants if enabled
      if (this.includeFields.variants) {
        variables.push('$variantsPageSize: Int = 25')
        const variantFields = [
          'id',
          'title',
          'sku',
          'barcode',
          'price',
          'compareAtPrice',
          'inventoryQuantity',
          `selectedOptions {
        name
        value
      }`
        ]

        // Add variant metafields if enabled
        if (this.includeFields.variantMetafields) {
          variantFields.push(`metafields(first: 10) {
        edges {
          node {
            id
            namespace
            key
            type
            value
          }
        }
      }`)
        }

        variantFields.push(`inventoryItem {
        id
        sku
        countryCodeOfOrigin
        tracked
        createdAt
        updatedAt
        measurement {
          weight {
            value
            unit
          }
        }
      }`)

        // Add variant media if media is enabled
        if (this.includeFields.media) {
          variantFields.push(`media(first: 10) {
        edges {
          node {
            mediaContentType
            alt
            preview {
              image {
                url
              }
            }

            ... on MediaImage {
              id
              image {
                url
              }
            }

            ... on Video {
              id
              duration
              status
              originalSource {
                url
                format
              }
            }

            ... on ExternalVideo {
              id
              originUrl
            }
          }
        }
      }`)
        }

        queryFields.push(`variants(first: $variantsPageSize) {
      edges {
        node {
          ${variantFields.join('\n          ')}
        }
      }
    }`)
      }

      // Add product media if enabled
      if (this.includeFields.media) {
        variables.push('$mediaPageSize: Int = 25')
        queryFields.push(`media(first: $mediaPageSize) {
      edges {
        node {
          mediaContentType
          alt
          preview {
            image {
              url
            }
          }

          ... on MediaImage {
            id
            image {
              url
            }
          }

          ... on Video {
            id
            duration
            status
            originalSource {
              url
              format
            }
          }

          ... on ExternalVideo {
            id
            originUrl
          }
        }
      }
    }`)
      }

      return `query ProductWithDetails(
  ${variables.join(',\n  ')}
) {
  product(id: $id) {
    ${queryFields.join('\n    ')}
  }
}`
    },
    getOrderQuery() {
      return `query OrderDetails($id: ID!) {
  order(id: $id) {
    id
    name
    email
    createdAt
    updatedAt
    cancelledAt
    cancelReason
    confirmed
    displayFinancialStatus
    displayFulfillmentStatus
    note
    phone
    tags
    totalPrice
    totalShippingPrice
    subtotalPrice
    totalTax
    currencyCode
    
    customer {
      id
      email
      firstName
      lastName
      phone
    }
    
    shippingAddress {
      address1
      address2
      city
      province
      country
      zip
      name
      phone
    }
    
    billingAddress {
      address1
      address2
      city
      province
      country
      zip
      name
      phone
    }
    
    lineItems(first: 25) {
      edges {
        node {
          id
          title
          quantity
          variantTitle
          sku
          vendor
          originalUnitPrice
          discountedUnitPrice
          product {
            id
            title
          }
          variant {
            id
            title
            sku
          }
        }
      }
    }
    
    fulfillments {
      id
      status
      trackingInfo {
        number
        url
        company
      }
      createdAt
      updatedAt
    }
    
    transactions(first: 10) {
      id
      kind
      status
      amount
      gateway
      createdAt
    }
  }
}`
    },
    async sendRequest() {
      if (!this.canSendRequest) return
      
      try {
        this.loadingResponse = true
        this.response = null
        
        const payload = {
          shop_id: this.selectedShop,
          api_version: this.apiVersion,
          query: this.graphqlQuery,
          variables: this.graphqlVariables,
          query_type: this.queryType
        }
        
        const response = await axios.post('admin/shopify/graphql/execute', payload)
        
        this.response = response.data
        
        // Switch to Response tab after response is received
        this.$nextTick(() => {
          // Response tab is index 4 (Body=0, Variables=1, Headers=2, Params=3, Response=4)
          this.activeTab = 4
          // Scroll to top of response
          setTimeout(() => {
            const responseElement = document.querySelector('.response-display')
            if (responseElement) {
              responseElement.scrollTop = 0
            }
          }, 100)
        })
        
        if (this.response.success) {
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'GraphQL query executed successfully',
            timer: 2000
          })
        }
      } catch (error) {
        this.response = {
          success: false,
          error: error.response?.data?.message || error.message || 'Request failed',
          details: error.response?.data
        }
        
        // Switch to Response tab even on error
        this.$nextTick(() => {
          // Response tab is index 4
          this.activeTab = 4
          // Scroll to top of response
          setTimeout(() => {
            const responseElement = document.querySelector('.response-display')
            if (responseElement) {
              responseElement.scrollTop = 0
            }
          }, 100)
        })
        
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: this.response.error
        })
      } finally {
        this.loadingResponse = false
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$swal({
          icon: 'success',
          title: 'Copied!',
          text: 'Content copied to clipboard',
          timer: 1500,
          showConfirmButton: false
        })
      }).catch(() => {
        this.$swal({
          icon: 'error',
          title: 'Failed',
          text: 'Failed to copy to clipboard'
        })
      })
    }
  }
}
</script>

<style scoped>
.graphql-tester {
  max-width: 1400px;
  margin: 0 auto;
}

.request-detail {
  margin-bottom: 15px;
}

.request-detail strong {
  font-size: 14px;
  color: #424242;
}

pre {
  border-radius: 4px;
  overflow-x: auto;
  font-size: 14px;
  line-height: 1.8;
  max-height: 500px;
  overflow-y: auto;
  padding: 15px !important;
}

.query-display,
.variables-display,
.headers-display {
  max-height: 600px;
  font-size: 15px;
  line-height: 1.9;
  margin: 0;
  padding: 20px !important;
}

.query-card {
  background-color: #f5f5f5;
}

code {
  font-family: 'Courier New', Courier, monospace;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: inherit;
  color: #424242;
}

.response-display {
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: auto;
  padding: 15px;
  width: 100%;
}

.response-card {
  max-height: 70vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.response-card .response-display {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
}

::v-deep .jv-container {
  font-size: 13px;
  max-height: 100%;
  overflow: visible;
}

::v-deep .jv-container .jv-code {
  padding: 15px;
}

/* Ensure json-viewer is scrollable */
::v-deep .jv-container.jv-boxed {
  max-height: 100%;
  overflow: visible;
}
</style>

